import { useState } from "react";
import { CheckCircle, XCircle, AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { ReceivedDocumentListItem } from "../types";
import type { BatchProgress, BatchAction } from "../hooks/useAcceptRejectDocuments";

interface AcceptRejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: ReceivedDocumentListItem[];
  action: BatchAction;
  isProcessing: boolean;
  progress: BatchProgress | null;
  onConfirm: (reason?: string) => void;
  onClose: () => void;
}

export function AcceptRejectDialog({
  open,
  onOpenChange,
  documents,
  action,
  isProcessing,
  progress,
  onConfirm,
  onClose,
}: AcceptRejectDialogProps) {
  const [reason, setReason] = useState("");

  const isReject = action === "reject";
  const title = isReject ? "Rechazar Documentos" : "Aceptar Documentos";
  const description = isReject
    ? `Vas a rechazar ${documents.length} documento(s). Esta acción se registrará en el SII.`
    : `Vas a aceptar ${documents.length} documento(s). Esta acción se registrará en el SII.`;

  const handleConfirm = () => {
    if (isReject && !reason.trim()) return;
    onConfirm(isReject ? reason : undefined);
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  // Show results when processing is complete
  const showResults = progress && progress.completed === progress.total && !isProcessing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isReject ? (
              <XCircle className="h-5 w-5 text-red-500" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
            {title}
          </DialogTitle>
          {!isProcessing && !showResults && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>

        {/* Pre-processing state */}
        {!isProcessing && !showResults && (
          <div className="space-y-4">
            {/* Document list preview */}
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex justify-between text-sm py-1 border-b last:border-b-0"
                >
                  <span>
                    Folio {doc.folio} - {doc.issuerName ?? doc.issuerTaxId}
                  </span>
                  <span className="text-muted-foreground">
                    ${parseFloat(doc.totalAmount).toLocaleString("es-CL")}
                  </span>
                </div>
              ))}
            </div>

            {/* Reason input for reject */}
            {isReject && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Motivo del rechazo *
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ingresa el motivo del rechazo..."
                  rows={3}
                />
                {reason.trim().length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    El motivo es obligatorio para rechazar documentos
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Processing state with progress bar */}
        {isProcessing && progress && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Procesando documento {progress.completed + 1} de {progress.total}...
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-primary h-2.5 rounded-full transition-all duration-300"
                style={{
                  width: `${(progress.completed / progress.total) * 100}%`,
                }}
              />
            </div>

            {/* Live results */}
            <div className="text-sm text-muted-foreground">
              {progress.succeeded > 0 && (
                <span className="text-green-600">
                  {progress.succeeded} exitoso(s)
                </span>
              )}
              {progress.succeeded > 0 && progress.failed > 0 && " | "}
              {progress.failed > 0 && (
                <span className="text-red-600">{progress.failed} error(es)</span>
              )}
            </div>
          </div>
        )}

        {/* Results state */}
        {showResults && progress && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-muted">
              {progress.failed === 0 ? (
                <CheckCircle className="h-8 w-8 text-green-500" />
              ) : progress.succeeded === 0 ? (
                <XCircle className="h-8 w-8 text-red-500" />
              ) : (
                <AlertCircle className="h-8 w-8 text-yellow-500" />
              )}
              <div>
                <p className="font-medium">
                  {progress.failed === 0
                    ? "Todos los documentos procesados correctamente"
                    : progress.succeeded === 0
                      ? "Error al procesar los documentos"
                      : "Procesamiento parcialmente exitoso"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {progress.succeeded} exitoso(s), {progress.failed} error(es)
                </p>
              </div>
            </div>

            {/* Detailed results */}
            <div className="max-h-48 overflow-y-auto border rounded-lg">
              {progress.results.map((result) => (
                <div
                  key={result.documentId}
                  className={`flex items-center justify-between p-2 text-sm border-b last:border-b-0 ${
                    result.success ? "bg-green-50" : "bg-red-50"
                  }`}
                >
                  <span>Folio {result.folio}</span>
                  {result.success ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      {isReject ? "Rechazado" : "Aceptado"}
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center gap-1">
                      <XCircle className="h-4 w-4" />
                      {result.error ?? "Error"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {!isProcessing && !showResults && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isReject && !reason.trim()}
                variant={isReject ? "destructive" : "default"}
              >
                {isReject ? "Rechazar" : "Aceptar"}
              </Button>
            </>
          )}
          {showResults && (
            <Button onClick={handleClose}>Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
